// ============================================================
// FSCS ELIGIBILITY PANEL — Independent component
// ============================================================
// Renders FSCS eligibility data for a customer. Only requires
// customerId and currentUser — no KYC engine dependency.
// Fetches all data from /api/customers/:id/fscs endpoints.
//
// Extracted from KYCModule to decouple FSCS (deposit protection)
// from KYC (identity verification).
// ============================================================

import { useState, useEffect, useRef } from 'react';
import { T, Badge, Card, Btn, SectionTitle, Input, font } from '../theme.js';
import { api } from '../api-client.js';

const fmtDateUK = d => {
  if (!d) return '\u2014';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

function formatApiError(err) {
  if (!err) return 'Unknown error';
  if (typeof err === 'string') return err;
  if (err.message) return err.message;
  if (err.error) return err.error;
  return JSON.stringify(err);
}

const ErrorBanner = ({ error, onDismiss, onRetry }) => {
  if (!error) return null;
  return (
    <div style={{ padding: '10px 14px', borderRadius: 6, background: T.redBg, border: `1px solid ${T.redBorder}`, marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <span style={{ fontSize: 13, color: T.red }}>{typeof error === 'string' ? error : error.message || JSON.stringify(error)}</span>
      <div style={{ display: 'flex', gap: 8 }}>
        {onRetry && <button onClick={onRetry} style={{ background: 'none', border: 'none', color: T.blue, cursor: 'pointer', fontSize: 12, fontWeight: 500, fontFamily: font }}>Retry</button>}
        {onDismiss && <button onClick={onDismiss} style={{ background: 'none', border: 'none', color: T.textMuted, cursor: 'pointer', fontSize: 12, fontFamily: font }}>&times;</button>}
      </div>
    </div>
  );
};

// ---- FSCS helpers ----

const FSCS_BADGE_MAP = {
  ELIGIBLE: { label: 'ELIGIBLE', color: 'green' },
  EXCLUDED: { label: 'EXCLUDED', color: 'red' },
  INELIGIBLE: { label: 'INELIGIBLE', color: 'red' },
  REVIEW: { label: 'REVIEW', color: 'amber' },
  PENDING: { label: 'PENDING', color: 'gray' },
  NOT_ASSESSED: { label: 'NOT ASSESSED', color: 'gray' },
};

const BRRD_LABELS = {
  SME: 'SME (Depositor Preference)',
  LARGE: 'Large Enterprise',
  UNKNOWN: 'BRRD Unknown \u2014 Review Required',
};

const DP22_CATEGORIES = [
  { key: 'credit_institution', label: 'Credit institution (FCA: Accepting deposits)', fcaCat: 'credit_institution' },
  { key: 'insurance', label: 'Insurance undertaking', fcaCat: 'insurance' },
  { key: 'investment_firm', label: 'Investment firm', fcaCat: 'investment_firm' },
  { key: 'cis_operator', label: 'CIS operator', fcaCat: 'cis_operator' },
  { key: 'public_authority', label: 'Public authority', fcaCat: 'public_authority' },
  { key: 'central_government', label: 'Central government', fcaCat: 'central_government' },
];

function deriveExclusionChecks(data) {
  const route = (data.auto_route || '').toLowerCase();
  const fcaCat = (data.fca_dp22_category || '').toLowerCase();
  const fcaRegulated = data.fca_regulated;
  const eligible = (data.auto_eligible || '').toUpperCase();
  const isFcaCat = (cat) => ['credit_institution', 'insurance', 'investment_firm', 'cis_operator'].includes(cat.key);
  return DP22_CATEGORIES.map(cat => {
    const isThisCategory = fcaCat === cat.fcaCat;
    if (isThisCategory && (eligible === 'EXCLUDED' || eligible === 'INELIGIBLE')) {
      return { ...cat, status: 'fail', explanation: `Identified as ${cat.label.toLowerCase()} on FCA register` };
    }
    if (isFcaCat(cat)) {
      if (fcaRegulated === false) return { ...cat, status: 'pass', explanation: 'Not an FCA-regulated firm in this category' };
      if (fcaRegulated === true && !isThisCategory) return { ...cat, status: 'pass', explanation: `FCA registered but not categorised as ${cat.label.toLowerCase()}` };
    }
    if (cat.key === 'public_authority') {
      if (route.includes('la')) {
        const laConfirmed = data.la_confirmed;
        if (laConfirmed === true) return { ...cat, status: 'fail', explanation: 'Confirmed as a local/public authority' };
        if (laConfirmed === false) return { ...cat, status: 'pass', explanation: 'Not identified as a public authority' };
      }
      if (route === 'company' || route === 'fca+ch' || route === 'fca' || route === 'ch') return { ...cat, status: 'pass', explanation: 'No public authority indicators found' };
    }
    if (cat.key === 'central_government') {
      if (route === 'company' || route === 'fca+ch' || route === 'fca' || route === 'ch') return { ...cat, status: 'pass', explanation: 'Not identified as a central government body' };
    }
    return { ...cat, status: 'not_checked', explanation: 'Not checked in this assessment' };
  });
}

function getDpRuleReference(eligible) {
  switch ((eligible || '').toUpperCase()) {
    case 'ELIGIBLE': return 'Eligible under PRA Depositor Protection Rule 2.2';
    case 'EXCLUDED': return 'Excluded under PRA Depositor Protection Rule 2.2(4)';
    case 'INELIGIBLE': return 'Ineligible under PRA Depositor Protection Rule 2.2';
    default: return null;
  }
}

const CHECK_ICONS = {
  pass: { symbol: '\u2713', color: T.green, bg: T.greenBg },
  fail: { symbol: '\u2717', color: T.red, bg: T.redBg },
  not_checked: { symbol: '\u2014', color: T.textMuted, bg: T.surfaceAlt },
};

function humaniseNote(note) {
  if (!note) return null;
  const n = note.trim();
  if (/accounts?\s*type\s*['"]?full/i.test(n) && /turnover/i.test(n)) return 'Companies House shows this company files full accounts. We could not extract turnover data to confirm their size. If turnover exceeds EUR 50m (~\u00a343m), BRRD should be set to Large Enterprise.';
  if (/accounts?\s*type\s*['"]?full/i.test(n)) return 'Companies House shows this company files full accounts, which may indicate a larger entity. Review recommended.';
  if (/turnover.*not\s*(available|found|extract)/i.test(n)) return 'Annual turnover could not be extracted from Companies House filings. RM should confirm whether turnover exceeds EUR 50m (~\u00a343m).';
  if (/fca.*not\s*found/i.test(n) || /no\s*fca\s*match/i.test(n)) return 'No match found on the FCA Financial Services Register. Entity is not FCA-regulated.';
  if (/companies\s*house.*not\s*found/i.test(n) || /ch.*lookup.*fail/i.test(n)) return 'Company not found on Companies House register. Verify the registration number is correct.';
  if (/low\s*confidence/i.test(n)) return 'Assessment confidence is low \u2014 automated checks were inconclusive. Manual review is required.';
  if (/brrd.*unknown/i.test(n) || /cannot\s*determine.*brrd/i.test(n)) return 'BRRD classification could not be determined automatically. RM must assess depositor preference status.';
  return n.replace(/\s*\|\s*/g, '. ').replace(/\.\s*\./g, '.');
}

const EvidenceRow = ({ label, value }) => (
  <tr>
    <td style={{ padding: '4px 8px 4px 0', color: T.textSecondary, fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap', verticalAlign: 'top' }}>{label}</td>
    <td style={{ padding: '4px 0', color: T.text, fontSize: 13, wordBreak: 'break-word' }}>{value}</td>
  </tr>
);

// ---- Main component ----

const FSCSPanel = ({ customerId, currentUser }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [showOverride, setShowOverride] = useState(false);
  const [ovrEligible, setOvrEligible] = useState('ELIGIBLE');
  const [ovrBrrd, setOvrBrrd] = useState('SME');
  const [ovrReason, setOvrReason] = useState('');
  const [ovrError, setOvrError] = useState(null);
  const [ovrSaving, setOvrSaving] = useState(false);
  const [eligExpanded, setEligExpanded] = useState(false);
  const [brrdSource, setBrrdSource] = useState('');
  const [brrdSaving, setBrrdSaving] = useState(false);
  const [showBrrdUpdate, setShowBrrdUpdate] = useState(false);

  const userRoles = currentUser?.roles || [];
  const canOverride = userRoles.includes('rm') || userRoles.includes('compliance');

  const loadData = async () => {
    try { setLoading(true); setError(null); const result = await api.get(`/customers/${customerId}/fscs`); setData(result); }
    catch (err) { setError(formatApiError(err)); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, [customerId]); // eslint-disable-line react-hooks/exhaustive-deps
  const cancelledRef = useRef(false);
  useEffect(() => () => { cancelledRef.current = true; }, []);

  const handleRefresh = async () => {
    try {
      setRefreshing(true); cancelledRef.current = false;
      await api.post(`/customers/${customerId}/fscs-refresh`);
      const poll = async (delay) => { await new Promise(r => setTimeout(r, delay)); if (cancelledRef.current) return null; return api.get(`/customers/${customerId}/fscs`); };
      const first = await poll(3000); if (cancelledRef.current) return;
      if (first?.effective_eligible && first.effective_eligible !== 'PENDING') { setData(first); setRefreshing(false); return; }
      const second = await poll(5000); if (cancelledRef.current) return;
      if (second?.effective_eligible && second.effective_eligible !== 'PENDING') { setData(second); setRefreshing(false); return; }
      setData({ ...(second || first), _pendingMessage: 'Assessment in progress \u2014 check back shortly' }); setRefreshing(false);
    } catch (err) { if (!cancelledRef.current) { setError(formatApiError(err)); setRefreshing(false); } }
  };

  const handleSetOverride = async () => {
    if (ovrReason.length < 50) { setOvrError('Justification must be at least 50 characters'); return; }
    try { setOvrSaving(true); setOvrError(null); const result = await api.put(`/customers/${customerId}/fscs-override`, { override_eligible: ovrEligible, override_brrd: ovrBrrd, override_reason: ovrReason }); setData(result); setShowOverride(false); setOvrReason(''); }
    catch (err) { setOvrError(formatApiError(err)); } finally { setOvrSaving(false); }
  };

  const handleClearOverride = async () => {
    const reason = prompt('Enter justification for clearing override (min 50 characters):');
    if (!reason || reason.length < 50) { setOvrError('Justification must be at least 50 characters'); return; }
    try { setOvrSaving(true); setOvrError(null); const result = await api.delete(`/customers/${customerId}/fscs-override`, { reason }); setData(result); }
    catch (err) { setOvrError(formatApiError(err)); } finally { setOvrSaving(false); }
  };

  const handleBrrdConfirm = async (brrdValue) => {
    const label = brrdValue === 'SME' ? '< EUR 50m' : '> EUR 50m';
    const today = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const justification = `RM confirmed turnover ${label}. Source: ${brrdSource || 'RM assessment'}. Date: ${today}`;
    if (justification.length < 50 && brrdSource.length < 10) { setOvrError('Please provide a source description (e.g. "Confirmed from audited accounts 2024")'); return; }
    try { setBrrdSaving(true); setOvrError(null); const result = await api.put(`/customers/${customerId}/fscs-override`, { override_eligible: data.effective_eligible || data.auto_eligible || 'ELIGIBLE', override_brrd: brrdValue, override_reason: justification }); setData(result); setBrrdSource(''); setShowBrrdUpdate(false); }
    catch (err) { setOvrError(formatApiError(err)); } finally { setBrrdSaving(false); }
  };

  if (loading) return <div style={{ padding: 20, color: T.textMuted }}>Loading FSCS data...</div>;
  if (error) return <ErrorBanner error={error} onDismiss={() => setError(null)} onRetry={loadData} />;
  if (!data) return null;

  const isNotAssessed = data.status === 'NOT_ASSESSED';
  const effectiveEligible = data.effective_eligible || data.auto_eligible || 'NOT_ASSESSED';
  const badge = FSCS_BADGE_MAP[effectiveEligible] || FSCS_BADGE_MAP.NOT_ASSESSED;
  const effectiveBrrd = data.effective_brrd || 'UNKNOWN';
  const isOverridden = data.override_active === true;
  const eligibilityOverridden = isOverridden && data.override_eligible != null && data.override_eligible !== data.auto_eligible;
  const brrdOnlyOverride = isOverridden && !eligibilityOverridden;
  const confidence = (data.auto_confidence || '').toUpperCase();
  const needsReview = confidence === 'LOW' || effectiveBrrd === 'UNKNOWN';
  const brrdDefinitive = isOverridden && data.override_brrd;
  const determinationComplete = (effectiveEligible === 'ELIGIBLE' || effectiveEligible === 'EXCLUDED' || effectiveEligible === 'INELIGIBLE') && effectiveBrrd !== 'UNKNOWN';
  const showConfidence = !brrdDefinitive && !determinationComplete && (confidence === 'LOW' || confidence === 'MEDIUM');
  const reviewDue = data.next_review_due ? new Date(data.next_review_due) : null;
  const now = new Date();
  const daysUntilReview = reviewDue ? Math.ceil((reviewDue - now) / (1000 * 60 * 60 * 24)) : null;
  const reviewWarning = daysUntilReview !== null && daysUntilReview <= 30;
  const exclusionChecks = isNotAssessed ? [] : deriveExclusionChecks(data);
  const dpRule = getDpRuleReference(effectiveEligible);
  const rawNotes = Array.isArray(data.auto_notes) ? data.auto_notes : (typeof data.auto_notes === 'string' ? data.auto_notes.split('|').map(s => s.trim()).filter(Boolean) : []);
  const brrdResolved = effectiveBrrd !== 'UNKNOWN';
  const humanNotes = rawNotes.map(humaniseNote).filter(note => {
    if (!note) return false;
    if (brrdResolved && /turnover|full accounts|BRRD should be set|EUR 50m|depositor preference/i.test(note)) return false;
    return true;
  });

  return (
    <div>
      {isNotAssessed ? (
        <Card>
          <div style={{ textAlign: 'center', padding: 20 }}>
            <span style={{ display: 'inline-block', padding: '6px 20px', borderRadius: 16, fontSize: 16, fontWeight: 700, background: T.surfaceAlt, color: T.textSecondary, border: `1px solid ${T.border}` }}>NOT ASSESSED</span>
            <p style={{ color: T.textSecondary, fontSize: 13, marginTop: 12 }}>{data.message}</p>
            <Btn onClick={handleRefresh} disabled={refreshing} style={{ marginTop: 12 }}>{refreshing ? 'Assessing...' : 'Run FSCS Assessment'}</Btn>
            {data._pendingMessage && (<div style={{ padding: '8px 12px', borderRadius: 6, background: T.amberBg, border: `1px solid ${T.amberBorder}`, marginTop: 12, fontSize: 13, color: T.amber, fontWeight: 500 }}>{data._pendingMessage}</div>)}
          </div>
        </Card>
      ) : (
        <>
          <Card style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
              <div>
                <div style={{ marginBottom: 8 }}>
                  <span style={{ display: 'inline-block', padding: '6px 20px', borderRadius: 16, fontSize: 16, fontWeight: 700, background: { green: T.greenBg, red: T.redBg, amber: T.amberBg, gray: T.surfaceAlt }[badge.color] || T.surfaceAlt, color: { green: T.green, red: T.red, amber: T.amber, gray: T.textSecondary }[badge.color] || T.textSecondary, border: `1px solid ${{ green: T.greenBorder, red: T.redBorder, amber: T.amberBorder, gray: T.border }[badge.color] || T.border}` }}>{badge.label}</span>
                  <span style={{ display: 'inline-block', padding: '6px 16px', borderRadius: 16, fontSize: 13, fontWeight: 600, marginLeft: 10, background: effectiveBrrd === 'UNKNOWN' ? T.amberBg : effectiveBrrd === 'LARGE' ? T.redBg : T.blueBg, color: effectiveBrrd === 'UNKNOWN' ? T.amber : effectiveBrrd === 'LARGE' ? T.red : T.blue, border: `1px solid ${effectiveBrrd === 'UNKNOWN' ? T.amberBorder : effectiveBrrd === 'LARGE' ? T.redBorder : T.blueBorder}` }}>{BRRD_LABELS[effectiveBrrd] || effectiveBrrd}</span>
                </div>
                <div style={{ fontSize: 12, color: T.textSecondary }}>{eligibilityOverridden ? `RM Override \u2014 ${data.override_by || 'unknown'} \u2014 ${fmtDateUK(data.override_at)}` : `Automated \u2014 ${fmtDateUK(data.auto_run_at)}`}</div>
                {dpRule && <div style={{ fontSize: 12, color: T.textMuted, marginTop: 4, fontStyle: 'italic' }}>{dpRule}</div>}
              </div>
              <Btn onClick={handleRefresh} disabled={refreshing} small variant="secondary">{refreshing ? 'Refreshing...' : 'Refresh'}</Btn>
            </div>
            {needsReview && !isOverridden && (<div style={{ padding: '10px 14px', borderRadius: 6, background: T.amberBg, border: `1px solid ${T.amberBorder}`, fontSize: 13, color: T.amber, fontWeight: 600 }}>RM Review Required {confidence === 'LOW' ? ' \u2014 assessment confidence is low' : ''}{effectiveBrrd === 'UNKNOWN' ? (confidence === 'LOW' ? ' and ' : ' \u2014 ') + 'BRRD classification could not be determined' : ''} {'\u2014'} see notes below</div>)}
            {eligibilityOverridden && (<div style={{ padding: '10px 14px', borderRadius: 6, background: T.amberBg, border: `1px solid ${T.amberBorder}`, marginTop: 8, fontSize: 13, color: T.amber, fontWeight: 500 }}>This determination has been manually overridden by {data.override_by || 'unknown'} on {fmtDateUK(data.override_at)}</div>)}
            {data._pendingMessage && (<div style={{ padding: '8px 12px', borderRadius: 6, background: T.amberBg, border: `1px solid ${T.amberBorder}`, marginTop: 8, fontSize: 13, color: T.amber, fontWeight: 500 }}>{data._pendingMessage}</div>)}
          </Card>

          <Card style={{ marginBottom: 16 }}>
            <SectionTitle>Eligibility Assessment</SectionTitle>
            {(() => {
              const failedChecks = exclusionChecks.filter(c => c.status === 'fail');
              const allPassed = failedChecks.length === 0 && exclusionChecks.every(c => c.status === 'pass');
              const hasUnchecked = exclusionChecks.some(c => c.status === 'not_checked');
              const checkedCount = exclusionChecks.filter(c => c.status !== 'not_checked').length;
              return (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: eligExpanded ? 12 : 0 }}>
                    <div style={{ fontSize: 13, color: T.text }}>
                      <span style={{ marginRight: 6 }}>PRA Depositor Protection 2.2(4) exclusion categories:</span>
                      {allPassed && <Badge color="green">{'\u2713'} All checked {'\u2014'} Eligible</Badge>}
                      {failedChecks.length > 0 && <Badge color="red">{'\u2717'} {failedChecks[0].label} {'\u2014'} Excluded</Badge>}
                      {!allPassed && failedChecks.length === 0 && hasUnchecked && <Badge color="gray">{checkedCount}/{exclusionChecks.length} checked</Badge>}
                    </div>
                    <button onClick={() => setEligExpanded(!eligExpanded)} style={{ background: 'none', border: 'none', color: T.blue, cursor: 'pointer', fontSize: 12, fontWeight: 500, fontFamily: font, padding: '4px 8px' }}>{eligExpanded ? 'Hide details \u25B2' : 'Show details \u25BC'}</button>
                  </div>
                  {eligExpanded && (
                    <table style={{ width: '100%', fontSize: 13, fontFamily: font, borderCollapse: 'collapse' }}>
                      <tbody>
                        {exclusionChecks.map((check, i) => {
                          const icon = CHECK_ICONS[check.status];
                          return (
                            <tr key={i} style={{ borderBottom: `1px solid ${T.borderLight}` }}>
                              <td style={{ padding: '8px 10px 8px 0', width: 28, textAlign: 'center', verticalAlign: 'top' }}><span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: '50%', fontSize: 13, fontWeight: 700, background: icon.bg, color: icon.color }}>{icon.symbol}</span></td>
                              <td style={{ padding: '8px 0', verticalAlign: 'top' }}><div style={{ fontWeight: 500, color: T.text }}>{check.label}</div><div style={{ fontSize: 12, color: T.textSecondary, marginTop: 2 }}>{check.explanation}</div></td>
                              <td style={{ padding: '8px 0 8px 12px', textAlign: 'right', whiteSpace: 'nowrap', verticalAlign: 'top' }}><Badge color={check.status === 'pass' ? 'green' : check.status === 'fail' ? 'red' : 'gray'}>{check.status === 'pass' ? 'Pass' : check.status === 'fail' ? 'Fail' : 'Not checked'}</Badge></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </>
              );
            })()}
          </Card>

          <Card style={{ marginBottom: 16 }}>
            <SectionTitle>BRRD Classification</SectionTitle>
            <div style={{ fontSize: 13, color: T.text, lineHeight: 1.6, marginBottom: 12 }}>
              {effectiveBrrd === 'SME' && <span>This depositor qualifies for depositor preference <span style={{ color: T.textSecondary, fontSize: 12 }}>(SCV Field 40: Yes)</span></span>}
              {effectiveBrrd === 'LARGE' && <span>This depositor does not qualify for depositor preference <span style={{ color: T.textSecondary, fontSize: 12 }}>(SCV Field 40: No)</span></span>}
              {effectiveBrrd === 'UNKNOWN' && (
                <div>
                  <div style={{ padding: '10px 14px', borderRadius: 6, background: T.amberBg, border: `1px solid ${T.amberBorder}`, fontSize: 13, color: T.amber, marginBottom: 12 }}>
                    BRRD classification requires turnover data.{data.ch_accounts_type ? ` Accounts type is '${data.ch_accounts_type}'` : ''}{data.ch_accounts_type === 'full' ? ' \u2014 turnover could not be extracted from filed accounts' : ''}. RM must confirm whether annual turnover exceeds EUR 50 million (~{'\u00a3'}43m).
                  </div>
                  {canOverride && !isOverridden && (
                    <div style={{ padding: '12px 14px', borderRadius: 6, background: T.surfaceAlt, border: `1px solid ${T.border}`, marginBottom: 12 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: T.textSecondary, marginBottom: 8 }}>Confirm BRRD Classification</div>
                      {ovrError && <div style={{ fontSize: 12, color: T.red, marginBottom: 8 }}>{ovrError}</div>}
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <input value={brrdSource} onChange={e => setBrrdSource(e.target.value)} placeholder="Source (e.g. Audited accounts 2024)" style={{ flex: 1, minWidth: 180, padding: '6px 10px', borderRadius: 6, border: `1px solid ${T.border}`, fontSize: 12, fontFamily: font, color: T.text, background: T.surface, boxSizing: 'border-box', outline: 'none' }} />
                        <Btn small onClick={() => handleBrrdConfirm('SME')} disabled={brrdSaving}>{brrdSaving ? '...' : '< EUR 50m (SME)'}</Btn>
                        <Btn small variant="danger" onClick={() => handleBrrdConfirm('LARGE')} disabled={brrdSaving}>{brrdSaving ? '...' : '> EUR 50m (Large Enterprise)'}</Btn>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <table style={{ width: '100%', fontSize: 13, fontFamily: font, borderCollapse: 'collapse' }}><tbody>
              {isOverridden && data.override_brrd ? (
                <tr><td style={{ padding: '4px 8px 4px 0', color: T.textSecondary, fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap', verticalAlign: 'top' }}>Confirmed by</td><td style={{ padding: '4px 0', color: T.text, fontSize: 13, wordBreak: 'break-word' }}><span>RM confirmed {'\u2014'} {data.override_by || 'unknown'} {'\u2014'} {fmtDateUK(data.override_at)}</span><div style={{ fontSize: 12, color: T.textSecondary, marginTop: 2 }}>{data.override_reason || 'No reason recorded'}</div>{canOverride && !showBrrdUpdate && <button onClick={() => setShowBrrdUpdate(true)} style={{ background: 'none', border: 'none', color: T.blue, cursor: 'pointer', fontSize: 12, fontWeight: 500, fontFamily: font, padding: '4px 0', marginTop: 4 }}>Update</button>}</td></tr>
              ) : (
                <>
                  <EvidenceRow label="CH Accounts Type" value={data.ch_accounts_type || '\u2014'} />
                  {data.ch_turnover_gbp != null && <EvidenceRow label="Turnover (GBP)" value={`\u00a3${Number(data.ch_turnover_gbp).toLocaleString()}`} />}
                  {data.ch_balance_sheet_gbp != null && <EvidenceRow label="Balance Sheet (GBP)" value={`\u00a3${Number(data.ch_balance_sheet_gbp).toLocaleString()}`} />}
                  {data.ch_employees != null && <EvidenceRow label="Employees" value={Number(data.ch_employees).toLocaleString()} />}
                  {showConfidence && <EvidenceRow label="Confidence" value={confidence || '\u2014'} />}
                </>
              )}
            </tbody></table>
            {canOverride && showBrrdUpdate && isOverridden && data.override_brrd && (
              <div style={{ padding: '12px 14px', borderRadius: 6, background: T.surfaceAlt, border: `1px solid ${T.border}`, marginTop: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: T.textSecondary, marginBottom: 8 }}>Update BRRD Classification</div>
                {ovrError && <div style={{ fontSize: 12, color: T.red, marginBottom: 8 }}>{ovrError}</div>}
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <input value={brrdSource} onChange={e => setBrrdSource(e.target.value)} placeholder="Source (e.g. Audited accounts 2024)" style={{ flex: 1, minWidth: 180, padding: '6px 10px', borderRadius: 6, border: `1px solid ${T.border}`, fontSize: 12, fontFamily: font, color: T.text, background: T.surface, boxSizing: 'border-box', outline: 'none' }} />
                  <Btn small onClick={() => handleBrrdConfirm('SME')} disabled={brrdSaving}>{brrdSaving ? '...' : '< EUR 50m (SME)'}</Btn>
                  <Btn small variant="danger" onClick={() => handleBrrdConfirm('LARGE')} disabled={brrdSaving}>{brrdSaving ? '...' : '> EUR 50m (Large Enterprise)'}</Btn>
                  <Btn small variant="ghost" onClick={() => setShowBrrdUpdate(false)}>Cancel</Btn>
                </div>
              </div>
            )}
          </Card>

          {(humanNotes.length > 0 || (isOverridden && data.override_brrd)) && (
            <Card style={{ marginBottom: 16 }}>
              <SectionTitle>Review Notes</SectionTitle>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: T.text, lineHeight: 1.7 }}>
                {humanNotes.map((note, i) => <li key={i} style={{ marginBottom: 6 }}>{note}</li>)}
                {isOverridden && data.override_brrd && <li style={{ marginBottom: 6 }}>BRRD classification confirmed by {data.override_by || 'unknown'} on {fmtDateUK(data.override_at)}: {data.override_reason || 'No reason recorded'}.</li>}
              </ul>
            </Card>
          )}

          <Card style={{ marginBottom: 16 }}>
            <SectionTitle>Review Schedule</SectionTitle>
            <div style={{ fontSize: 13, color: T.text }}>Next review due: <strong>{fmtDateUK(data.next_review_due)}</strong>{reviewWarning && <span style={{ color: T.amber, marginLeft: 8, fontWeight: 600 }}>({daysUntilReview <= 0 ? 'Overdue' : `Due in ${daysUntilReview} days`})</span>}</div>
            {data.last_reviewed_at && <div style={{ fontSize: 12, color: T.textSecondary, marginTop: 4 }}>Last reviewed: {fmtDateUK(data.last_reviewed_at)} {data.last_reviewed_by ? `by ${data.last_reviewed_by}` : ''}</div>}
          </Card>

          {canOverride && !brrdOnlyOverride && (
            <Card>
              <SectionTitle>Override Determination</SectionTitle>
              {ovrError && <ErrorBanner error={ovrError} onDismiss={() => setOvrError(null)} />}
              {isOverridden && (
                <div style={{ marginBottom: 12 }}>
                  <table style={{ width: '100%', fontSize: 13, fontFamily: font, borderCollapse: 'collapse' }}><tbody>
                    <EvidenceRow label="Override Eligibility" value={data.override_eligible} />
                    <EvidenceRow label="Override BRRD" value={data.override_brrd} />
                    <EvidenceRow label="Reason" value={data.override_reason} />
                    <EvidenceRow label="Set By" value={data.override_by || '\u2014'} />
                    <EvidenceRow label="Set At" value={fmtDateUK(data.override_at)} />
                  </tbody></table>
                  <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                    <Btn variant="secondary" small onClick={() => setShowOverride(true)}>Change Override</Btn>
                    <Btn variant="danger" small onClick={handleClearOverride} disabled={ovrSaving}>{ovrSaving ? 'Clearing...' : 'Clear Override'}</Btn>
                  </div>
                </div>
              )}
              {(!isOverridden || showOverride) && (
                <div style={{ marginTop: isOverridden ? 12 : 0 }}>
                  <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: T.textSecondary, marginBottom: 4 }}>Eligibility</label>
                      <select value={ovrEligible} onChange={e => setOvrEligible(e.target.value)} style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: `1px solid ${T.border}`, fontSize: 13, fontFamily: font }}>
                        <option value="ELIGIBLE">Eligible</option><option value="EXCLUDED">Excluded</option><option value="INELIGIBLE">Ineligible</option>
                      </select>
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: T.textSecondary, marginBottom: 4 }}>BRRD Classification</label>
                      <select value={ovrBrrd} onChange={e => setOvrBrrd(e.target.value)} style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: `1px solid ${T.border}`, fontSize: 13, fontFamily: font }}>
                        <option value="SME">SME (Depositor Preference)</option><option value="LARGE">Large Enterprise</option>
                      </select>
                    </div>
                  </div>
                  <Input label={`Justification (${ovrReason.length}/50 chars min)`} value={ovrReason} onChange={setOvrReason} placeholder="Provide detailed justification for override (minimum 50 characters)..." multiline />
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <Btn onClick={handleSetOverride} disabled={ovrSaving || ovrReason.length < 50}>{ovrSaving ? 'Saving...' : 'Submit Override'}</Btn>
                    {showOverride && <Btn variant="ghost" onClick={() => setShowOverride(false)}>Cancel</Btn>}
                  </div>
                </div>
              )}
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default FSCSPanel;
export { FSCSPanel };
